import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import './TesterPremiumPage.css';

function TesterPremiumPage() {
  const isSignedIn = !!localStorage.getItem('userToken');
  const [trialDays, setTrialDays] = useState(0);

  useEffect(() => {
    let active = true;
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/billing/plans`)
      .then((response) => {
        if (!active) return;
        const premium = (response.data?.plans || []).find((plan) => plan.code === 'premium');
        setTrialDays(Number(premium?.trialDays) || 0);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const primaryHref = isSignedIn ? '/dashboard?tab=billing' : '/signup';
  const primaryLabel = isSignedIn
    ? 'Manage Subscription'
    : (trialDays > 0 ? `Start ${trialDays}-Day Free Trial` : 'Create Account');

  return (
    <HelmetProvider>
      <div className="tester-premium-page page-shell">
        <Helmet>
          <title>VixPremium - Cloud Signals, MT5 Copier, EA Imports, and Builder Export</title>
          <meta
            name="description"
            content="VixPremium runs your strategies as live cloud signals, copies them into MetaTrader 5, hosts imported MQL5 EAs on our servers, and unlocks VixAlgoBuilder saves with MQ5 export."
          />
          <meta property="og:title" content="VixPremium" />
          <meta
            property="og:description"
            content="Live cloud signals, MT5 copy trading, imported EA hosting, live account tracking, MQ5 export, and the member community."
          />
          <meta property="og:image" content="https://vixalgotrading.com/tester-premium-cover.svg" />
          <meta property="og:url" content="https://vixalgotrading.com/tester-premium" />
        </Helmet>

        <section className="tester-premium-hero">
          <div className="tester-premium-copy">
            <span className="tag">VixPremium</span>
            <h1 className="page-title">
              Run your strategies live in the cloud, copy them into MetaTrader, and keep the whole
              track record honest.
            </h1>
            <p className="page-lead">
              VixPremium started as the Builder's paid layer and is now the whole pipeline: design a
              strategy or upload an EA you already own, deploy it as a live cloud signal that runs
              without MetaTrader or a VPS, copy its trades straight into your own MT5 account, and
              watch that account's real results next to the signal that produced them.
            </p>
            <div className="tester-premium-price">
              {trialDays > 0 ? (
                <>
                  <strong>{trialDays}-day free trial</strong>
                  <span>then $2.99/month. Cancel anytime during the trial and you won't be charged.</span>
                </>
              ) : (
                <>
                  <strong>$2.99/month</strong>
                  <span>VixPremium subscription. Cancel anytime.</span>
                </>
              )}
            </div>
            <div className="tester-premium-actions">
              <Link to={primaryHref} className="primary-cta">{primaryLabel}</Link>
              <Link to="/signals" className="ghost-cta tester-premium-ghost">Browse Signals</Link>
              <Link to="/import-ea" className="ghost-cta tester-premium-ghost">Import an EA</Link>
              <Link to="/tester" className="ghost-cta tester-premium-ghost">Open Builder</Link>
              <Link to="/vixai" className="ghost-cta tester-premium-ghost">VixAI</Link>
              <Link to="/community" className="ghost-cta tester-premium-ghost">Open Community</Link>
            </div>
            <div className="tester-premium-badges">
              <div className="tester-premium-badge">
                <strong>Signals</strong>
                <span>your strategies live in the cloud</span>
              </div>
              <div className="tester-premium-badge">
                <strong>Copier</strong>
                <span>signals mirrored into your MT5</span>
              </div>
              <div className="tester-premium-badge">
                <strong>Imports</strong>
                <span>run an EA you already own</span>
              </div>
              <div className="tester-premium-badge">
                <strong>Live MT5</strong>
                <span>track a real account from anywhere</span>
              </div>
              <div className="tester-premium-badge">
                <strong>100</strong>
                <span>saved strategies, with MQ5 export</span>
              </div>
              <div className="tester-premium-badge">
                <strong>Community</strong>
                <span>share, import, and discuss strategies</span>
              </div>
            </div>
          </div>

          <div className="tester-premium-visual">
            <img
              src="/tester-premium-cover.svg"
              alt="VixPremium preview"
              className="tester-premium-cover"
            />
          </div>
        </section>

        <section className="tester-premium-grid">
          <article className="section-card">
            <h3>Cloud Signals</h3>
            <p className="muted-text">
              Deploy a saved strategy and it runs on live market data in the cloud, evaluated at
              every candle close. Nothing stays open on your machine.
            </p>
            <ul className="tester-premium-list">
              <li>Telegram, email, or in-app alerts carrying entry, stop, target, and why it fired.</li>
              <li>A track record that starts the day you deploy, with recent history already filled in.</li>
              <li>Keep it private, or publish it and let others follow.</li>
            </ul>
          </article>

          <article className="section-card">
            <h3>Copy to MetaTrader 5</h3>
            <p className="muted-text">
              One EA on one chart mirrors every signal you own or follow into your own account. It
              asks the server what should be open and matches your terminal to it.
            </p>
            <ul className="tester-premium-list">
              <li>Close MetaTrader for a day and it catches up rather than missing trades.</li>
              <li>Lot size is yours: fixed, a percentage of your balance, or scaled from the creator's.</li>
              <li>Stops and targets follow the signal as it trails them.</li>
              <li>It only ever closes positions it opened. Your manual trades are untouched.</li>
            </ul>
          </article>

          <article className="section-card">
            <h3>Bring your own EA</h3>
            <p className="muted-text">
              Upload an MQL5 EA you already own and we run it on our servers as a live signal —
              no Builder rebuild, no VPS, no rewriting it in someone else's language.
            </p>
            <ul className="tester-premium-list">
              <li>Backtest the import first, so you know it runs before it goes live.</li>
              <li>The same ledger, alerts, and copier as a Builder strategy.</li>
              <li>Your source is analysed into a program and is never stored or served back.</li>
            </ul>
          </article>

          <article className="section-card">
            <h3>Live MT5 tracking</h3>
            <p className="muted-text">
              Point a real or demo account at your dashboard and follow it from your phone —
              balance, equity, trades, and drawdown.
            </p>
            <ul className="tester-premium-list">
              <li>Works from the copier alone; no Builder EA needs to be on the chart.</li>
              <li>Where you also run the same strategy as a cloud signal, the two are compared.</li>
              <li>Self-reported by your own terminal, and labelled that way everywhere it appears.</li>
            </ul>
          </article>

          <article className="section-card">
            <h3>Builder, VixAI, and Community</h3>
            <ul className="tester-premium-list">
              <li>Save far more strategy variants while you iterate across symbols and timeframes.</li>
              <li>Export supported strategies as MQ5 EA source code directly from the Builder.</li>
              <li>Use VixAI to draft custom inputs, non-indicator logic, visuals, and info panels.</li>
              <li>Share strategies, reports, files, and improvement threads with other members.</li>
              <li>Your saved workspace follows your account, not a browser.</li>
            </ul>
          </article>

          <article className="section-card">
            <h3>How access works</h3>
            <ul className="tester-premium-list">
              <li>Your plan is linked to your account, not to a single browser session.</li>
              <li>When PayPal activates or cancels the subscription, your VixPremium permissions update automatically.</li>
              <li>Copying checks your plan on every poll, so access follows your subscription.</li>
              <li>Free users keep the Builder with a smaller save limit.</li>
            </ul>
          </article>
        </section>

        <section className="tester-premium-compare section-card">
          <div>
            <h2>Free vs premium</h2>
            <p className="muted-text">
              The free plan is for exploring the Builder. VixPremium is for actually running
              something — live signals, copying into MetaTrader, hosted EA imports, and a persistent
              strategy library.
            </p>
          </div>
          <div className="tester-premium-table">
            <div className="tester-premium-column">
              <p className="tester-premium-column-title">Free</p>
              <ul className="tester-premium-list">
                <li>Builder access</li>
                <li>1 saved strategy</li>
                <li>3 MQ5 exports per month</li>
                <li>Browse published signals</li>
              </ul>
            </div>
            <div className="tester-premium-column premium">
              <p className="tester-premium-column-title">VixPremium</p>
              <ul className="tester-premium-list">
                <li>Everything in Free</li>
                <li>Live cloud signals with alerts</li>
                <li>Copy signals into MetaTrader 5</li>
                <li>Import and host your own MQL5 EAs</li>
                <li>Live MT5 account tracking</li>
                <li>100 saved strategies</li>
                <li>VixAI strategy drafts</li>
                <li>MQ5 EA source export</li>
                <li>Premium Community access</li>
                <li>$2.99/month</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="tester-premium-cta section-card">
          <div>
            <h2>Ready to activate VixPremium?</h2>
            <p className="muted-text">
              Use the billing tab to subscribe, manage status, and keep signals, copying, imports,
              Builder, and Community entitlements in sync.
            </p>
          </div>
          <div className="tester-premium-actions">
            <Link to={primaryHref} className="primary-cta">{primaryLabel}</Link>
            <Link to="/login" className="ghost-cta tester-premium-ghost">Sign In</Link>
          </div>
        </section>
      </div>
    </HelmetProvider>
  );
}

export default TesterPremiumPage;
